import apiClient from '../apiClient';
import {
  buildCampaignFormData,
  omitRulesText,
  sanitizeGiveawayState,
} from './campaignFormValues';

export function getApiErrorMessage(error, fallback = 'Request failed') {
  try {
    if (error == null) return fallback;
    if (typeof error === 'string' && error.trim()) return error;

    const responseData = error.response && error.response.data;
    if (responseData && typeof responseData.error === 'string' && responseData.error.trim()) {
      return responseData.error;
    }
    if (responseData && typeof responseData.message === 'string' && responseData.message.trim()) {
      return responseData.message;
    }
    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
    return fallback;
  } catch (_) {
    return fallback;
  }
}

/** Extra context when the API returns Mongoose User cast errors (invalid createdBy / user refs). */
export function formatGiveawayCampaignLoadError(error) {
  const msg = getApiErrorMessage(error, 'Failed to load giveaway campaign details.');
  if (/Cast to ObjectId failed.*User/i.test(msg)) {
    return `${msg} The campaign detail request failed, and the list fallback did not return this campaign. This usually indicates invalid user references on the campaign (for example createdBy) or a backend bug when populating User. Fix the campaign record or adjust the GET /admin/giveaway/campaigns/:id handler so invalid user ids do not crash the request.`;
  }
  return msg;
}

/**
 * POST multipart create. Send shoesImage as a File; other fields as form strings.
 * Response data.shoesImage is the public URL under /uploads/campaignImages/.
 */
export async function createGiveawayCampaign(payload) {
  const formData = buildCampaignFormData(payload);
  const response = await apiClient.post('/admin/giveaway/campaigns', formData);
  const campaign = response.data && response.data.data;
  return campaign ? omitRulesText(campaign) : campaign;
}

export async function listGiveawayCampaigns({ page = 1, limit = 20, status } = {}) {
  const response = await apiClient.get('/admin/giveaway/campaigns', {
    params: {
      page,
      limit,
      ...(status ? { status } : {}),
    },
  });
  const body = response.data;
  if (!body || !Array.isArray(body.data)) return body;
  return { ...body, data: body.data.map(omitRulesText) };
}

export async function getGiveawayCampaign(id) {
  const response = await apiClient.get(`/admin/giveaway/campaigns/${id}`);
  const campaign = response.data && response.data.data;
  return campaign ? omitRulesText(campaign) : campaign;
}

const MAX_LIST_PAGES_FOR_FALLBACK = 40;
const LIST_PAGE_SIZE_FOR_FALLBACK = 50;

/**
 * Finds a campaign by id by walking admin list pages.
 * Used when GET /campaigns/:id fails (e.g. backend populates User with invalid createdBy).
 */
export async function getGiveawayCampaignFromList(id) {
  const idStr = id == null ? '' : String(id);
  if (!idStr) return null;

  let page = 1;
  while (page <= MAX_LIST_PAGES_FOR_FALLBACK) {
    const res = await listGiveawayCampaigns({ page, limit: LIST_PAGE_SIZE_FOR_FALLBACK });
    const rows = Array.isArray(res.data) ? res.data : [];
    const found = rows.find((row) => row && String(row._id) === idStr);
    if (found) return found;

    const pg = res.pagination || {};
    if (pg.hasNextPage === false) break;
    if (pg.hasNextPage === true) {
      page += 1;
      continue;
    }
    const totalPages = Number(pg.totalPages);
    if (Number.isFinite(totalPages) && totalPages > 0) {
      if (page >= totalPages) break;
      page += 1;
      continue;
    }
    if (rows.length < LIST_PAGE_SIZE_FOR_FALLBACK) break;
    page += 1;
  }
  return null;
}

/**
 * Prefer detail endpoint; if it errors, try list aggregate (often avoids broken User populate on detail).
 */
export async function getGiveawayCampaignWithFallback(id) {
  let detailError;
  try {
    const direct = await getGiveawayCampaign(id);
    if (direct) return direct;
  } catch (e) {
    detailError = e;
  }

  try {
    const fromList = await getGiveawayCampaignFromList(id);
    if (fromList) return fromList;
  } catch (e) {
    if (detailError) throw detailError;
    throw e;
  }

  throw detailError || new Error('Campaign not found');
}

/**
 * PATCH multipart update. Include shoesImage File only when replacing the image;
 * server returns the new public shoesImage URL.
 */
export async function updateGiveawayCampaign(id, payload) {
  const formData = buildCampaignFormData(payload);
  const response = await apiClient.patch(`/admin/giveaway/campaigns/${id}`, formData);
  const campaign = response.data && response.data.data;
  return campaign ? omitRulesText(campaign) : campaign;
}

export async function getGiveawayParticipants(id, page = 1, limit = 20) {
  const response = await apiClient.get(`/admin/giveaway/campaigns/${id}/participants`, {
    params: { page, limit },
  });
  return response.data;
}

/** Admin: full prize / shipping profile for a participant user. */
export async function getAdminUserPrizeProfile(userId) {
  const response = await apiClient.get(`/admin/giveaway/users/${userId}/prize-profile`);
  return response.data;
}

/**
 * Select winner(s):
 * - one: { userId }
 * - many (up to remaining slots): { userIds: [id1, id2, ...] }
 */
export async function selectGiveawayWinner(id, payload) {
  const response = await apiClient.post(`/admin/giveaway/campaigns/${id}/winner`, payload);
  const data = response.data && response.data.data;
  return data ? omitRulesText(data) : data;
}

export async function getCurrentGiveawayState() {
  const response = await apiClient.get('/giveaway/current');
  return sanitizeGiveawayState(response.data);
}

export async function getGiveawayWinners({ page = 1, limit = 20 } = {}) {
  const response = await apiClient.get('/giveaway/winners', {
    params: { page, limit },
  });
  return response.data;
}

/**
 * POST multipart to save a giveaway banner image.
 * Expects form field name `image` (file). Response: { url, path, filename }.
 */
export async function uploadGiveawayBanner(file) {
  const formData = new FormData();
  formData.append('image', file, file.name);
  const response = await apiClient.post('/uploads/giveawayBanners', formData);
  const body = response.data;
  if (body && typeof body === 'object' && typeof body.url === 'string' && body.url) {
    return body;
  }
  throw new Error('Unexpected upload response');
}
